import { useEffect } from 'react';
import type { RefObject } from 'react';

type UseAssetViewSettleParams = {
  asset_container_ref: RefObject<HTMLDivElement | null>;
  asset_index: number;
  on_asset_view_settle: (asset_index: number) => void;
};

const ASSET_VIEW_CENTER_ROOT_MARGIN = '-35% 0px -35% 0px';
const ASSET_VIEW_SETTLE_DELAY_MS = 900;

export function use_asset_view_settle({
  asset_container_ref,
  asset_index,
  on_asset_view_settle,
}: UseAssetViewSettleParams) {
  useEffect(() => {
    const asset_container = asset_container_ref.current;
    let settle_timeout_id: number | null = null;
    let is_asset_visible = false;
    let is_scroll_event_listener_active = false;

    if (!asset_container || !('IntersectionObserver' in window)) {
      return;
    }

    const clear_pending_settle_notification = () => {
      if (!settle_timeout_id) {
        return;
      }

      window.clearTimeout(settle_timeout_id);
      settle_timeout_id = null;
    };

    const notify_asset_view_settle = () => {
      clear_pending_settle_notification();
      on_asset_view_settle(asset_index);
    };

    const schedule_asset_view_settle = () => {
      if (settle_timeout_id) {
        return;
      }

      settle_timeout_id = window.setTimeout(
        notify_asset_view_settle,
        ASSET_VIEW_SETTLE_DELAY_MS,
      );
    };

    const handle_scroll_event = () => {
      if (!is_asset_visible) {
        return;
      }

      clear_pending_settle_notification();
      schedule_asset_view_settle();
    };

    const enable_scroll_settle_delay = () => {
      if (is_scroll_event_listener_active) {
        return;
      }

      window.addEventListener('scroll', handle_scroll_event, { passive: true });
      is_scroll_event_listener_active = true;
    };

    const disable_scroll_settle_delay = () => {
      if (!is_scroll_event_listener_active) {
        return;
      }

      window.removeEventListener('scroll', handle_scroll_event);
      is_scroll_event_listener_active = false;
    };

    const asset_intersection_observer = new IntersectionObserver(
      intersection_entries => {
        is_asset_visible = intersection_entries.some(
          intersection_entry => intersection_entry.isIntersecting,
        );

        if (!is_asset_visible) {
          clear_pending_settle_notification();
          disable_scroll_settle_delay();
          return;
        }

        enable_scroll_settle_delay();
        schedule_asset_view_settle();
      },
      { rootMargin: ASSET_VIEW_CENTER_ROOT_MARGIN, threshold: 0 },
    );

    asset_intersection_observer.observe(asset_container);

    return () => {
      clear_pending_settle_notification();
      disable_scroll_settle_delay();
      asset_intersection_observer.disconnect();
    };
  }, [asset_container_ref, asset_index, on_asset_view_settle]);
}
